import React, { createContext, useContext, useEffect, useState } from 'react'
import type { Session, User } from '@supabase/supabase-js'
import { getSupabaseClient } from '../lib/supabaseClient'

interface Profile {
  id: string
  role: 'user' | 'admin'
  full_name: string | null
  email: string | null
  phone: string | null
  address: string | null
  membership_status: 'none' | 'active' | 'one_time' | 'cancelled'
  stripe_customer_id: string | null
}

interface AuthState {
  session: Session | null
  user: User | null
  profile: Profile | null
  loading: boolean
  signUp: (email: string, password: string, fullName: string) => Promise<{ error: string | null }>
  signIn: (email: string, password: string) => Promise<{ error: string | null }>
  signInWithMagicLink: (email: string) => Promise<{ error: string | null }>
  signOut: () => Promise<void>
  refreshProfile: () => Promise<void>
  demoSignIn: (role: 'user' | 'admin', member?: boolean) => void
  isAdmin: boolean
  isMember: boolean
}

const AuthContext = createContext<AuthState | undefined>(undefined)

export function AuthProvider({ children }: { children: React.ReactNode }) {
  const supabase = getSupabaseClient()
  const [session, setSession] = useState<Session | null>(null)
  const [profile, setProfile] = useState<Profile | null>(null)
  const [loading, setLoading] = useState(true)

  const fetchProfile = async (userId: string) => {
    const { data } = await supabase
      .from('profiles')
      .select('*')
      .eq('id', userId)
      .single()
    if (data) setProfile(data as Profile)
  }

  useEffect(() => {
    supabase.auth.getSession().then(({ data: { session: s } }) => {
      setSession(s)
      if (s?.user) fetchProfile(s.user.id)
      setLoading(false)
    })

    const { data: { subscription } } = supabase.auth.onAuthStateChange((_event, s) => {
      setSession(s)
      if (s?.user) {
        fetchProfile(s.user.id)
      } else {
        setProfile(null)
      }
      setLoading(false)
    })

    return () => subscription.unsubscribe()
  }, [])

  const signUp = async (email: string, password: string, fullName: string) => {
    const { error } = await supabase.auth.signUp({
      email,
      password,
      options: { data: { full_name: fullName } },
    })
    return { error: error?.message ?? null }
  }

  const signIn = async (email: string, password: string) => {
    const { error } = await supabase.auth.signInWithPassword({ email, password })
    return { error: error?.message ?? null }
  }

  const signInWithMagicLink = async (email: string) => {
    const { error } = await supabase.auth.signInWithOtp({
      email,
      options: { emailRedirectTo: `${window.location.origin}/portal` },
    })
    return { error: error?.message ?? null }
  }

  const signOut = async () => {
    await supabase.auth.signOut()
    setProfile(null)
  }

  const refreshProfile = async () => {
    if (session?.user) await fetchProfile(session.user.id)
  }

  const demoSignIn = (role: 'user' | 'admin', member?: boolean) => {
    const demoId = role === 'admin' ? 'demo-admin-000' : member ? 'demo-member-000' : 'demo-user-000'
    const demoUser = {
      id: demoId,
      email: role === 'admin' ? 'admin@demo.hudsonmyhome.com' : 'user@demo.hudsonmyhome.com',
      aud: 'authenticated',
      app_metadata: {},
      user_metadata: { full_name: role === 'admin' ? 'Hudson Admin' : 'Bailey Peterson' },
      created_at: new Date().toISOString(),
    } as unknown as User

    const demoSession = {
      access_token: 'demo-token',
      refresh_token: 'demo-refresh',
      expires_in: 99999,
      token_type: 'bearer',
      user: demoUser,
    } as unknown as Session

    setSession(demoSession)
    setProfile({
      id: demoId,
      role,
      full_name: role === 'admin' ? 'Hudson Admin' : 'Bailey Peterson',
      email: demoUser.email ?? null,
      phone: null,
      address: role === 'user' ? '1234 Maple Dr, Ankeny, IA 50023' : null,
      membership_status: role === 'admin' || member ? 'active' : 'none',
      stripe_customer_id: null,
    })
    setLoading(false)
  }

  return (
    <AuthContext.Provider
      value={{
        session,
        user: session?.user ?? null,
        profile,
        loading,
        signUp,
        signIn,
        signInWithMagicLink,
        signOut,
        refreshProfile,
        demoSignIn,
        isAdmin: profile?.role === 'admin',
        isMember: profile?.membership_status === 'active' || profile?.role === 'admin',
      }}
    >
      {children}
    </AuthContext.Provider>
  )
}

export function useAuth() {
  const ctx = useContext(AuthContext)
  if (!ctx) throw new Error('useAuth must be used inside AuthProvider')
  return ctx
}
